import { get, post, put } from "aws-amplify/api";
import "containers/common/List.css";
import Moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Col, Form, Nav, Row } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Select from "react-select";
import { createColumnHelper } from "@tanstack/react-table";
import { PacmanLoader } from "react-spinners";
import { GenericTable } from "components/GenericTable";
import { createElementIdFactory, createReusableTableElementIds } from "utils/elementIds";
import { PAGE_SIZES } from "utils/pageSizeConfig";

const columnHelper = createColumnHelper();

function SalesFeedQueue({ isAuthenticated, user }) {
    const [isLoading, setIsLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [savingNewRecord, setSavingNewRecord] = useState(false);
    const [sales, setSales] = useState([]);

    const [feeddate, setFeeddate] = useState(null);
    const [feedDateOptions, setFeedDateOptions] = useState([]);
    const [selectedFeedDate, setSelectedFeedDate] = useState(null);

    const [cityscope, setCityscope] = useState(null);
    const [streetname, setStreetname] = useState("");
    const [suburb, setSuburb] = useState("");
    const [state, setState] = useState(null);
    const [matched, setMatched] = useState(null);
    const [keyed, setKeyed] = useState(null);
    const [saledatefrom, setSaledatefrom] = useState(null);
    const [saledateto, setSaledateto] = useState(null);
    const [id, setId] = useState("");
    const [salepricefrom, setSalepricefrom] = useState("");
    const [salepriceto, setSalepriceto] = useState("");
    const [transfernumber, setTransfernumber] = useState("");
    const [checkedby, setCheckedby] = useState("");

    const [selectedCityscope, setSelectedCityscope] = useState(null);
    const [selectedKeyed, setSelectedKeyed] = useState({ value: null, label: "Both" });
    const [selectedMatched, setSelectedMatched] = useState({ value: null, label: "Both" });
    const [cityscopeOptions, setCityscopeOptions] = useState([]);
    const [selectedState, setSelectedState] = useState(null);
    const [stateOptions] = useState([
        { value: "ACT", label: "ACT" },
        { value: "NSW", label: "NSW" },
        { value: "NT", label: "NT" },
        { value: "QLD", label: "QLD" },
        { value: "SA", label: "SA" },
        { value: "TAS", label: "TAS" },
        { value: "VIC", label: "VIC" },
        { value: "WA", label: "WA" },
    ]);
    const [selectedCategories, setSelectedCategories] = useState(null);
    const [categoryOptions] = useState([
        { value: "CO", label: "CO" },
        { value: "BU", label: "BU" },
        { value: "HO", label: "HO" },
        { value: "UN", label: "UN" },
    ]);
    const [category, setCategory] = useState(null);
    const [selectedAgentsAdvice, setSelectedAgentsAdvice] = useState(null);
    const [agentsAdviceOptions] = useState([
        { value: true, label: "Only AA" },
        { value: false, label: "Exclude AA" },
    ]);
    const [agentsAdvice, setAgentsAdvice] = useState(null);
    const [matchScopeOptions] = useState([
        { value: "cityscope", label: "Match to Cityscope" },
        { value: "property", label: "Match to Property" },
    ]);
    const [selectedMatchScope, setSelectedMatchScope] = useState({ value: "cityscope", label: "Match to Cityscope" });
    const [matchScope, setMatchScope] = useState("cityscope");

    const createId = useMemo(() => createElementIdFactory("sales-feed-queue"), []);
    const tableElementIds = useMemo(() => createReusableTableElementIds(createId, "main"), [createId]);

    const filterSelectStyles = useMemo(() => ({
        container: (base) => ({ ...base, width: "100%" }),
        control: (base) => ({ ...base, minHeight: 38 }),
    }), []);

    const formatter = useMemo(() => new Intl.NumberFormat("en-AU", {
        style: "currency",
        currency: "AUD",
        minimumFractionDigits: 0,
        currencyDisplay: "symbol",
    }), []);

    const getSales = useCallback(async (filter) => {
        const queryParams = Object.fromEntries(
            Object.entries(filter).filter(([, value]) => value !== null && value !== undefined && value !== "")
        );

        try {
            const { body } = await get({
                apiName: "colin",
                path: "/salesfeed",
                options: { queryParams },
            }).response;
            return await body.json();
        } catch (error) {
            alert(`${error}. Please refresh the page (F5) to try again.`);
            return [];
        }
    }, []);

    const getFeedDates = useCallback(async () => {
        try {
            const { body } = await get({
                apiName: "colin",
                path: "/salesfeed/dropdown",
            }).response;
            const options = await body.json();
            if (options && options.length > 0) {
                setFeedDateOptions(options);
                setSelectedFeedDate(options[0]);
                setFeeddate(options[0].value);
            }
        } catch {
            console.log("Error getting feed dates");
        }
    }, []);

    const getCityscopes = useCallback(async () => {
        try {
            const { body } = await get({
                apiName: "colin",
                path: "/cityscopes/dropdown",
            }).response;
            const options = await body.json();
            if (options) {
                setCityscopeOptions(options);
            }
        } catch {
            console.log("Error getting cityscopes");
        }
    }, []);

    useEffect(() => {
        if (!isAuthenticated) {
            return;
        }

        document.title = "Sales Feed Queue";

        const load = async () => {
            await Promise.all([getCityscopes(), getFeedDates()]);
        };

        load();
    }, [isAuthenticated, getCityscopes, getFeedDates]);

    const updateSaleCell = useCallback((rowIndex, key, value, markModified = true) => {
        setSales((prevSales) => prevSales.map((sale, index) => {
            if (index !== rowIndex) {
                return sale;
            }
            return {
                ...sale,
                [key]: value,
                ...(markModified ? { modified: true } : {}),
            };
        }));
    }, []);

    const commitReferenceNote = useCallback((rowIndex) => {
        setSales((prevSales) => prevSales.map((sale, index) => {
            if (index !== rowIndex) {
                return sale;
            }

            if (sale.reference_note_draft === undefined) {
                return sale;
            }

            const draftValue = sale.reference_note_draft ?? "";
            const currentValue = sale.reference_note ?? "";
            const hasChanged = draftValue !== currentValue;

            return {
                ...sale,
                reference_note: hasChanged ? draftValue : sale.reference_note,
                reference_note_draft: undefined,
                ...(hasChanged ? { modified: true } : {}),
            };
        }));
    }, []);

    const saveRecord = useCallback(async (record) => {
        const payload = {
            ...record,
            last_edited_by: user,
        };

        if (payload.new_record === true) {
            const { body } = await post({
                apiName: "colin",
                path: "/salesfeed/new",
                options: {
                    body: payload,
                },
            }).response;
            return await body.json();
        }

        const { body } = await put({
            apiName: "colin",
            path: `/salesfeed/${payload.draft_sales_feeds_id}`,
            options: {
                body: payload,
            },
        }).response;
        return await body.json();
    }, [user]);

    const saveModifiedRows = useCallback(async () => {
        if (savingNewRecord) {
            return;
        }

        const pendingRows = sales
            .map((sale, index) => ({ sale, index }))
            .filter(({ sale }) => sale.modified === true);

        if (pendingRows.length === 0) {
            return;
        }

        try {
            for (const pending of pendingRows) {
                const result = await saveRecord(pending.sale);
                setSales((prevSales) => prevSales.map((sale, rowIndex) => {
                    if (rowIndex !== pending.index) {
                        return sale;
                    }

                    const updatedRow = {
                        ...sale,
                        modified: false,
                    };

                    if (sale.new_record === true) {
                        updatedRow.new_record = false;
                        updatedRow.draft_sales_feeds_id = result.id;
                        setSavingNewRecord(false);
                    }

                    return updatedRow;
                }));
            }
        } catch (error) {
            alert(`Error saving row: ${error}`);
            setSavingNewRecord(false);
        }
    }, [sales, saveRecord, savingNewRecord]);

    const handleSubmit = useCallback(async (event) => {
        event.preventDefault();

        setHasSearched(true);
        setIsLoading(true);
        try {
            const results = await getSales({
                cityscope,
                feeddate: feeddate !== null ? Moment(feeddate).local().format("YYYY-MM-DD") : null,
                streetname,
                suburb,
                state,
                matched,
                keyed,
                saledatefrom: saledatefrom !== null ? Moment(saledatefrom).local().format("YYYY-MM-DD") : null,
                saledateto: saledateto !== null ? Moment(saledateto).local().format("YYYY-MM-DD") : null,
                salepricefrom,
                salepriceto,
                transfernumber,
                checkedby,
                id,
                category,
                agentsAdvice,
                matchScope,
            });
            setSales(results || []);
        } finally {
            setIsLoading(false);
        }
    }, [
        agentsAdvice,
        category,
        checkedby,
        cityscope,
        feeddate,
        getSales,
        id,
        keyed,
        matchScope,
        matched,
        saledatefrom,
        saledateto,
        salepricefrom,
        salepriceto,
        state,
        streetname,
        suburb,
        transfernumber,
    ]);

    const handleReset = useCallback(async (event) => {
        event.preventDefault();

        setSelectedFeedDate(feedDateOptions.length > 0 ? feedDateOptions[0] : null);
        setFeeddate(feedDateOptions.length > 0 ? feedDateOptions[0].value : null);
        setSelectedCityscope(null);
        setCityscope(null);
        setStreetname("");
        setSuburb("");
        setSelectedState(null);
        setState(null);
        setSelectedMatched({ value: null, label: "Both" });
        setMatched(null);
        setSelectedKeyed({ value: null, label: "Both" });
        setKeyed(null);
        setSaledatefrom(null);
        setSaledateto(null);
        setId("");
        setSalepricefrom("");
        setSalepriceto("");
        setTransfernumber("");
        setCheckedby("");
        setSelectedCategories(null);
        setCategory(null);
        setSelectedAgentsAdvice(null);
        setAgentsAdvice(null);
        setSelectedMatchScope({ value: "cityscope", label: "Match to Cityscope" });
        setMatchScope("cityscope");
        setHasSearched(false);
        setSales([]);
    }, [feedDateOptions]);

    const handleBatchKeyed = useCallback(async () => {
        if (!hasSearched || sales.length === 0) {
            return;
        }

        const hasSelectedRows = sales.some((sale) => sale.selected === true);
        if (!hasSelectedRows) {
            return;
        }

        const confirmed = window.confirm("Are you sure you want to batch update all selected records?");
        if (!confirmed) {
            return;
        }

        setSales((prevSales) => prevSales.map((sale) => {
            if (!sale.selected) {
                return sale;
            }
            return {
                ...sale,
                keyed: true,
                modified: true,
                last_edited_by: user,
            };
        }));
    }, [hasSearched, sales, user]);

    useEffect(() => {
        const hasModifiedRows = sales.some((sale) => sale.modified === true);
        if (hasModifiedRows) {
            saveModifiedRows();
        }
    }, [sales, saveModifiedRows]);

    const columns = useMemo(() => [
        columnHelper.accessor("selected", {
            header: "",
            size: 56,
            enableSorting: false,
            cell: (info) => (
                <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: 32 }}>
                    <input
                        type="checkbox"
                        checked={Boolean(info.getValue())}
                        onChange={(event) => updateSaleCell(info.row.index, "selected", event.target.checked)}
                        aria-label="Select row"
                        style={{ width: 16, height: 16, margin: 0, cursor: "pointer" }}
                    />
                </div>
            ),
            meta: { align: "center" },
        }),
        columnHelper.accessor("draft_sales_feeds_id", {
            header: "ID",
            size: 60,
            meta: { align: "center" },
        }),
        columnHelper.accessor("keyed", {
            header: "Checked",
            size: 60,
            cell: (info) => (
                <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: 32 }}>
                    <input
                        type="checkbox"
                        checked={Boolean(info.getValue())}
                        onChange={(event) => {
                            updateSaleCell(info.row.index, "keyed", event.target.checked);
                            updateSaleCell(info.row.index, "last_edited_by", user, false);
                        }}
                        aria-label="Checked row"
                        style={{ width: 16, height: 16, margin: 0, cursor: "pointer" }}
                    />
                </div>
            ),
            meta: { align: "center" },
        }),
        columnHelper.accessor("last_edited_by", {
            header: "Checked By",
            size: 100,
        }),
        columnHelper.accessor("created_date", {
            header: "Feed Date",
            size: 90,
            sortingFn: "datetime",
            cell: (info) => (info.getValue() ? Moment(info.getValue()).local().format("DD-MM-YYYY") : null),
            meta: { align: "center" },
        }),
        columnHelper.accessor("reference_note", {
            header: "Cityscope",
            size: 130,
            cell: (info) => {
                const row = info.row.original;
                return (
                    <div>
                        {(row.cityscopes || []).map((cityscopeItem) => (
                            <div key={`${row.draft_sales_feeds_id}-cityscope-${cityscopeItem.cityscope}`}>{cityscopeItem.cityscope}</div>
                        ))}
                        <Form.Control
                            value={row.reference_note_draft ?? row.reference_note ?? ""}
                            onChange={(event) => updateSaleCell(info.row.index, "reference_note_draft", event.target.value, false)}
                            onBlur={() => commitReferenceNote(info.row.index)}
                        />
                        {(row.properties || []).map((property) => (
                            <div key={`${row.draft_sales_feeds_id}-property-${property.property_key}`}>
                                <a
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    href={`../properties/${property.property_key}`}
                                >
                                    {property.property_key}
                                </a>
                            </div>
                        ))}
                    </div>
                );
            },
        }),
        columnHelper.accessor("category_major_id", {
            header: "Category",
            size: 90,
            cell: (info) => (
                <div>
                    {info.row.original.category_major_id}
                    <br />
                    {info.row.original.agents_advice && <p className="text-danger">Agents Advice</p>}
                </div>
            ),
        }),
        columnHelper.accessor("unit", {
            header: "Unit",
            size: 60,
        }),
        columnHelper.accessor("street_address", {
            header: "Street Address",
            size: 220,
            cell: (info) => (
                <div>
                    {info.row.original.street_address}
                    <br />
                    {info.row.original.suburb} {info.row.original.state} {info.row.original.postcode}
                </div>
            ),
        }),
        columnHelper.accessor("lot_plan", {
            header: "Title Details",
            size: 120,
            cell: (info) => {
                const row = info.row.original;
                if (row.state !== "ACT") {
                    return (
                        <div>
                            {(row.lot_plan || "").replaceAll("/", " / ")}
                            <br />
                            {(row.volume_folio || "").replaceAll("/", " / ")}
                        </div>
                    );
                }

                return (
                    <div>
                        {(row.volume_folio || "").replaceAll("/", " / ")}
                        <br />
                        {(row.rpd || "").replaceAll(":", " : ")}
                    </div>
                );
            },
        }),
        columnHelper.accessor("sale_date", {
            header: "Sale Date",
            size: 140,
            sortingFn: "datetime",
            cell: (info) => (
                <div>
                    CD: {info.row.original.sale_date ? Moment(info.row.original.sale_date).local().format("DD/MM/YYYY") : ""}
                    <br />
                    TD: {info.row.original.poss_date ? Moment(info.row.original.poss_date).local().format("DD/MM/YYYY") : ""}
                </div>
            ),
        }),
        columnHelper.accessor("sale_amount", {
            header: "Sale Price",
            size: 120,
            meta: { align: "right" },
            cell: (info) => {
                const value = info.getValue();
                return value || value === 0 ? formatter.format(value) : null;
            },
        }),
        columnHelper.accessor("sale_code", {
            header: "Sale Code",
            size: 100,
        }),
        columnHelper.accessor("transfer_number", {
            header: "Transfer #",
            size: 120,
        }),
        columnHelper.accessor("vendor", {
            header: "Buyer Vendor",
            size: 260,
            cell: (info) => (
                <div>
                    Buyer: {info.row.original.purchaser}
                    <br />
                    {info.row.original.purchaser_add1} {info.row.original.purchaser_add2} {info.row.original.purchaser_add3}
                    <br />
                    Vendor: {info.row.original.vendor}
                </div>
            ),
        }),
        columnHelper.accessor("agency", {
            header: "Agency",
            size: 170,
            cell: (info) => (
                <div>
                    {info.row.original.agency}
                    <br />
                    {info.row.original.agency_phone && <div>Phone {info.row.original.agency_phone}</div>}
                </div>
            ),
        }),
        columnHelper.accessor("attributes", {
            header: "Attributes",
            size: 150,
        }),
    ], [commitReferenceNote, formatter, updateSaleCell, user]);

    if (!isAuthenticated) {
        return <div className="SalesList" />;
    }

    return (
        <div id={createId("page")} className="SalesList">
            <div id={createId("content")} className="Sales">
                <form id={createId("filter", "form")} onSubmit={handleSubmit} onReset={handleReset} className="mb-3">
                    <Row id={createId("filter", "row", "primary")} className="g-3 align-items-end">
                        <Col xs={12} md={6} xl>
                            <Form.Group controlId="feeddate">
                                <Form.Label>Feed Date</Form.Label>
                                <Select
                                    inputId={createId("input", "feed-date")}
                                    instanceId={createId("instance", "feed-date")}
                                    value={selectedFeedDate}
                                    onChange={(selectedOption) => {
                                        setSelectedFeedDate(selectedOption);
                                        setFeeddate(selectedOption?.value ?? null);
                                    }}
                                    options={feedDateOptions}
                                    styles={filterSelectStyles}
                                    isClearable
                                />
                            </Form.Group>
                        </Col>
                        <Col xs={12} md={6} xl>
                            <Form.Group controlId="cityscope">
                                <Form.Label className="text-right">Cityscope</Form.Label>
                                <Select
                                    inputId={createId("input", "cityscope")}
                                    instanceId={createId("instance", "cityscope")}
                                    value={selectedCityscope}
                                    onChange={(selectedOption) => {
                                        setSelectedCityscope(selectedOption);
                                        setCityscope(selectedOption ? selectedOption.map((item) => item.value).join(",") : null);
                                    }}
                                    options={cityscopeOptions}
                                    styles={filterSelectStyles}
                                    isMulti
                                />
                            </Form.Group>
                        </Col>
                        <Col xs={12} md={6} xl>
                            <Form.Group controlId="matchscope">
                                <Form.Label className="text-right">Match to</Form.Label>
                                <Select
                                    inputId={createId("input", "match-scope")}
                                    instanceId={createId("instance", "match-scope")}
                                    value={selectedMatchScope}
                                    onChange={(selectedOption) => {
                                        setSelectedMatchScope(selectedOption);
                                        setMatchScope(selectedOption?.value ?? null);
                                    }}
                                    options={matchScopeOptions}
                                    styles={filterSelectStyles}
                                />
                            </Form.Group>
                        </Col>
                        <Col xs={12} md={6} xl>
                            <Form.Group controlId="matched">
                                <Form.Label className="text-right">Matched?</Form.Label>
                                <Select
                                    inputId={createId("input", "matched")}
                                    instanceId={createId("instance", "matched")}
                                    value={selectedMatched}
                                    onChange={(selectedOption) => {
                                        setSelectedMatched(selectedOption);
                                        setMatched(selectedOption?.value ?? null);
                                    }}
                                    options={[
                                        { value: true, label: "Matched" },
                                        { value: false, label: "Unmatched" },
                                        { value: null, label: "Both" },
                                    ]}
                                    styles={filterSelectStyles}
                                />
                            </Form.Group>
                        </Col>
                        <Col xs={12} md={6} xl>
                            <Form.Group controlId="category">
                                <Form.Label className="text-right">Category</Form.Label>
                                <Select
                                    inputId={createId("input", "category")}
                                    instanceId={createId("instance", "category")}
                                    value={selectedCategories}
                                    onChange={(selectedOption) => {
                                        setSelectedCategories(selectedOption);
                                        setCategory(selectedOption ? selectedOption.map((item) => item.value).join(",") : null);
                                    }}
                                    options={categoryOptions}
                                    styles={filterSelectStyles}
                                    isMulti
                                    isClearable
                                />
                            </Form.Group>
                        </Col>
                        <Col xs={12} md={6} xl>
                            <Form.Group controlId="keyed">
                                <Form.Label className="text-right">Checked?</Form.Label>
                                <Select
                                    inputId={createId("input", "keyed")}
                                    instanceId={createId("instance", "keyed")}
                                    value={selectedKeyed}
                                    onChange={(selectedOption) => {
                                        setSelectedKeyed(selectedOption);
                                        setKeyed(selectedOption?.value ?? null);
                                    }}
                                    options={[
                                        { value: true, label: "Checked" },
                                        { value: false, label: "Not Checked" },
                                        { value: null, label: "Both" },
                                    ]}
                                    styles={filterSelectStyles}
                                />
                            </Form.Group>
                        </Col>
                        <Col xs={12} md={6} xl>
                            <Form.Group controlId="checkedby">
                                <Form.Label className="text-right">Checked By</Form.Label>
                                <Form.Control
                                    id={createId("input", "checked-by")}
                                    type="text"
                                    value={checkedby}
                                    onChange={(event) => setCheckedby(event.target.value)}
                                    placeholder="Containing"
                                />
                            </Form.Group>
                        </Col>
                        <Col xs={12} md={6} xl>
                            <Form.Group controlId="id">
                                <Form.Label className="text-right">ID</Form.Label>
                                <Form.Control
                                    id={createId("input", "record-id")}
                                    type="text"
                                    value={id}
                                    onChange={(event) => setId(event.target.value)}
                                    placeholder="ID"
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row id={createId("filter", "row", "secondary")} className="g-3 align-items-end mt-0">
                        <Col xs={12} md={6} xl>
                            <Form.Group controlId="streetname">
                                <Form.Label className="text-right">Street Name</Form.Label>
                                <Form.Control
                                    id={createId("input", "street-name")}
                                    type="text"
                                    value={streetname}
                                    onChange={(event) => setStreetname(event.target.value)}
                                    placeholder="Containing"
                                />
                            </Form.Group>
                        </Col>
                        <Col xs={12} md={6} xl>
                            <Form.Group controlId="suburb">
                                <Form.Label className="text-right">Suburb</Form.Label>
                                <Form.Control
                                    id={createId("input", "suburb")}
                                    type="text"
                                    value={suburb}
                                    onChange={(event) => setSuburb(event.target.value)}
                                    placeholder="Containing"
                                />
                            </Form.Group>
                        </Col>
                        <Col xs={12} md={6} xl>
                            <Form.Group controlId="state">
                                <Form.Label className="text-right">State</Form.Label>
                                <Select
                                    inputId={createId("input", "state")}
                                    instanceId={createId("instance", "state")}
                                    value={selectedState}
                                    onChange={(selectedOption) => {
                                        setSelectedState(selectedOption);
                                        setState(selectedOption?.value ?? null);
                                    }}
                                    options={stateOptions}
                                    styles={filterSelectStyles}
                                    isClearable
                                />
                            </Form.Group>
                        </Col>
                        <Col xs={12} md={6} xl>
                            <Form.Group controlId="saledatefrom">
                                <Form.Label>Sale Date From</Form.Label>
                                <DatePicker
                                    id={createId("input", "sale-date-from")}
                                    selected={saledatefrom}
                                    onChange={(date) => setSaledatefrom(date ? new Date(date) : null)}
                                    dateFormat="dd/MM/yyyy"
                                    className="form-control w-100"
                                    isClearable
                                />
                            </Form.Group>
                        </Col>
                        <Col xs={12} md={6} xl>
                            <Form.Group controlId="saledateto">
                                <Form.Label>Sale Date To</Form.Label>
                                <DatePicker
                                    id={createId("input", "sale-date-to")}
                                    selected={saledateto}
                                    onChange={(date) => setSaledateto(date ? new Date(date) : null)}
                                    dateFormat="dd/MM/yyyy"
                                    className="form-control w-100"
                                    isClearable
                                />
                            </Form.Group>
                        </Col>
                        <Col xs={12} md={6} xl>
                            <Form.Group controlId="salepricefrom">
                                <Form.Label className="text-right">Sale Price From</Form.Label>
                                <Form.Control
                                    id={createId("input", "sale-price-from")}
                                    type="text"
                                    value={salepricefrom}
                                    onChange={(event) => setSalepricefrom(event.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col xs={12} md={6} xl>
                            <Form.Group controlId="salepriceto">
                                <Form.Label className="text-right">Sale Price To</Form.Label>
                                <Form.Control
                                    id={createId("input", "sale-price-to")}
                                    type="text"
                                    value={salepriceto}
                                    onChange={(event) => setSalepriceto(event.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col xs={12} md={6} xl>
                            <Form.Group controlId="transfernumber">
                                <Form.Label className="text-right">Transfer #</Form.Label>
                                <Form.Control
                                    id={createId("input", "transfer-number")}
                                    type="text"
                                    value={transfernumber}
                                    onChange={(event) => setTransfernumber(event.target.value)}
                                    placeholder="Containing"
                                />
                            </Form.Group>
                        </Col>
                        <Col xs={12} md={6} xl>
                            <Form.Group controlId="agentsadvice">
                                <Form.Label className="text-right">Agents Advice</Form.Label>
                                <Select
                                    inputId={createId("input", "agents-advice")}
                                    instanceId={createId("instance", "agents-advice")}
                                    value={selectedAgentsAdvice}
                                    onChange={(selectedOption) => {
                                        setSelectedAgentsAdvice(selectedOption);
                                        setAgentsAdvice(selectedOption?.value ?? null);
                                    }}
                                    options={agentsAdviceOptions}
                                    styles={filterSelectStyles}
                                    isClearable
                                />
                            </Form.Group>
                        </Col>
                        <Col xs={12} md={6} xl>
                            <div className="d-flex gap-2 w-100">
                                <Button id={createId("button", "search")} type="submit" variant="primary" className="flex-fill">Search</Button>
                                <Button id={createId("button", "reset")} type="reset" className="flex-fill">Reset</Button>
                            </div>
                        </Col>
                    </Row>
                </form>

                {hasSearched && (
                    <Row id={createId("actions", "row")}>
                        <Col>
                            <Nav className="justify-content-start" id={createId("actions", "selection")}>
                                <Nav.Item>
                                    <Button id={createId("button", "select-all")} variant="primary" type="button" size="sm" onClick={() => {
                                        setSales((prevSales) => prevSales.map((sale) => ({ ...sale, selected: true })));
                                    }}>
                                        Select All
                                    </Button>{" "}
                                </Nav.Item>
                                <Nav.Item>
                                    <Button id={createId("button", "deselect-all")} variant="primary" type="button" size="sm" onClick={() => {
                                        setSales((prevSales) => prevSales.map((sale) => ({ ...sale, selected: false })));
                                    }}>
                                        Deselect All
                                    </Button>{" "}
                                </Nav.Item>
                                <Nav.Item>
                                    <Button id={createId("button", "batch-update-checked")} variant="primary" type="button" size="sm" onClick={handleBatchKeyed}>
                                        Batch Update Checked
                                    </Button>
                                </Nav.Item>
                            </Nav>
                        </Col>
                    </Row>
                )}

                {hasSearched && (
                    <Row id={createId("results", "summary", "row")}>
                        <Col>
                            <b id={createId("results", "summary", "count")}>{sales.length} of {sales.length > 0 ? sales[0].result_count : "0"} records available.</b>
                        </Col>
                    </Row>
                )}

                <Row className="show-Container">
                    <Col sm={12} md={12} lg={12}>
                        {hasSearched && isLoading ? (
                            <div className="outer-div">
                                <div className="sweet-loading inner-div">
                                    <PacmanLoader
                                        cssOverride={{ display: "block", margin: "0 auto" }}
                                        size={20}
                                        color="#e03c31"
                                        loading={isLoading}
                                    />
                                </div>
                            </div>
                        ) : hasSearched ? (
                            <GenericTable
                                columns={columns}
                                data={sales}
                                section="sales-feed"
                                pageSize={PAGE_SIZES.rpSalesFeed}
                                enablePagination={true}
                                hasHeaderSorting={true}
                                elementIds={tableElementIds}
                            />
                        ) : null
                        }
                    </Col>
                </Row>
            </div>
        </div>
    );
}

export default SalesFeedQueue;